import { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, spacing } from '@ardoq/design-tokens';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { Text, text2Bold } from '@ardoq/typography';
import { LabelHelpIconWithPopover } from './LabelHelpIcon';
import { ICON_SELECTOR } from '@ardoq/icons';

export type LabelProps = {
  isDisabled?: boolean;
  indicator?: 'optional' | 'priority' | 'required';
  id?: string;
  inputId?: string;
  width?: CSSProperties['width'];
  popoverHelpContent?: ReactNode;
  /**
   * Optional locale for translating the rendered indicator label
   * (`(optional)` / `(required)` / `(priority)`). When omitted, falls back
   * to English so callers that have not yet adopted the i18n flow remain
   * unchanged.
   */
  i18nLocale?: I18nLocale;
};

const INDICATOR_KEY: Record<NonNullable<LabelProps['indicator']>, string> = {
  optional: 'forms:label.indicatorOptional',
  required: 'forms:label.indicatorRequired',
  priority: 'forms:label.indicatorPriority',
};

const getIndicatorText = (
  indicator: NonNullable<LabelProps['indicator']>,
  i18nLocale: I18nLocale
): string => t(i18nLocale, INDICATOR_KEY[indicator], indicator);

const LabelStyle = styled.div<{
  $isDisabled?: boolean;
  $width?: React.CSSProperties['width'];
}>`
  display: flex;
  align-items: center;
  gap: ${spacing.s4};
  ${text2Bold};
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.textDisabled : colors.textDefault};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `};
  width: ${({ $width }) => $width ?? '100%'};
`;

export const LABEL_SELECTOR = LabelStyle.toString();

export const Label = ({
  children,
  id,
  indicator,
  inputId,
  isDisabled,
  width,
  popoverHelpContent,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: PropsWithChildren<LabelProps>) => {
  return (
    <LabelStyle $isDisabled={isDisabled} $width={width}>
      <label id={id} htmlFor={inputId}>
        {children}
      </label>
      {popoverHelpContent && (
        <LabelHelpIconWithPopover content={popoverHelpContent} />
      )}
      {indicator && (
        <Text color="textSubtle">
          ({getIndicatorText(indicator, i18nLocale)})
        </Text>
      )}
    </LabelStyle>
  );
};
